const STORAGE_KEY = "hackwerk-cookie-consent";

type ConsentState = "granted" | "denied";

/** Private mode / storage disabled can make localStorage throw on access, not just
    return null - without the guard that would surface as an uncaught error and skip
    consent handling entirely, instead of just falling back to "no stored decision". */
function getStoredConsent(): ConsentState | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

function storeConsent(state: ConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // Nothing we can do; the decision still applies for the rest of this session.
  }
}

interface NavigatorWithPrivacySignals extends Navigator {
  globalPrivacyControl?: boolean;
}

/**
 * True if the browser is sending an opt-out privacy signal: the legacy Do Not Track
 * header/API, or Global Privacy Control (globalprivacycontrol.org) - the actively
 * maintained successor to DNT, sent by Brave and Firefox/DuckDuckGo-browser by default,
 * and a recognised legal opt-out signal under CPRA/CCPA. Either one skips the automatic
 * banner popup the same way; "Cookie-Einstellungen" in the footer still lets a visitor
 * explicitly opt in afterwards if they want to.
 */
function hasPrivacyOptOutSignal(nav: Navigator): boolean {
  return nav.doNotTrack === "1" || (nav as NavigatorWithPrivacySignals).globalPrivacyControl === true;
}

function buildBanner(onDecision: (state: ConsentState) => void): HTMLElement {
  const banner = document.createElement("div");
  banner.className = "cookie-banner";
  banner.setAttribute("role", "dialog");
  banner.setAttribute("aria-label", "Cookie-Einstellungen");
  banner.tabIndex = -1;

  const text = document.createElement("p");
  text.className = "cookie-banner-text";
  text.append(
    "Wir nutzen ein selbst gehostetes Analyse-Tool (Matomo), um zu sehen, wie unsere Seite genutzt wird. Das ist freiwillig und hilft uns beim Verbessern der Seite. ",
  );
  const link = document.createElement("a");
  link.href = "/datenschutz.html";
  link.textContent = "Mehr dazu in der Datenschutzerklärung.";
  text.append(link);

  const actions = document.createElement("div");
  actions.className = "cookie-banner-actions";

  const decline = document.createElement("button");
  decline.type = "button";
  decline.className = "btn btn-ghost";
  decline.textContent = "Ablehnen";
  decline.addEventListener("click", () => onDecision("denied"));

  const accept = document.createElement("button");
  accept.type = "button";
  accept.className = "btn btn-primary";
  accept.textContent = "Akzeptieren";
  accept.addEventListener("click", () => onDecision("granted"));

  actions.append(decline, accept);
  banner.append(text, actions);
  return banner;
}

/**
 * Opt-in cookie consent for Matomo (TTDSG §25 requires consent before
 * non-essential storage/tracking, not just an opt-out). onGranted only
 * fires once the visitor has actually agreed, or had already agreed
 * in a previous visit. onRevoked fires if a visitor who is currently
 * being tracked reopens the banner (via "Cookie-Einstellungen") and
 * withdraws consent - withdrawal has to be as effective as giving it,
 * not just skip loading analytics on the *next* page load.
 */
export function initCookieConsent(onGranted: () => void, onRevoked?: () => void): void {
  let active = false;

  function showBanner(): void {
    if (document.querySelector(".cookie-banner")) return;
    document.body.classList.add("has-cookie-banner");
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const banner = buildBanner((state) => {
      storeConsent(state);
      banner.remove();
      document.body.classList.remove("has-cookie-banner");
      // Restore focus to whatever opened the banner (e.g. the "Cookie-Einstellungen"
      // button) so keyboard/screen-reader users aren't dropped back at the page top.
      previouslyFocused?.focus();
      if (state === "granted") {
        if (!active) {
          active = true;
          onGranted();
        }
      } else if (active) {
        active = false;
        onRevoked?.();
      }
    });
    banner.classList.add("enter-rise");
    document.body.append(banner);
    banner.focus();
  }

  document.getElementById("cookieSettings")?.addEventListener("click", showBanner);

  if (hasPrivacyOptOutSignal(navigator)) return;

  const stored = getStoredConsent();
  if (stored === "granted") {
    active = true;
    onGranted();
    return;
  }
  if (stored === "denied") return;

  showBanner();
}
